import { useState, useEffect, useCallback } from 'react';
import { useTranslate } from 'react-admin';
import { Alert, AlertTitle, Button, Collapse, LinearProgress, Box } from '@mui/material';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import { wsClient } from '../../websocket/WebSocketClient';

interface ConnectionStatusBannerProps {
  /** Position of the banner */
  position?: 'top' | 'bottom';
}

/**
 * ConnectionStatusBanner - Shows connection status when disconnected or reconnecting
 *
 * Features:
 * - Shows warning banner when WebSocket connection is lost
 * - Shows reconnecting state with progress indicator
 * - Infinite reconnection attempts
 * - Shows incremental message after prolonged disconnection suggesting page refresh
 * - Auto-hides when connection is restored
 */
export function ConnectionStatusBanner({ position = 'top' }: ConnectionStatusBannerProps) {
  const translate = useTranslate();
  const [connectionState, setConnectionState] = useState<'connected' | 'disconnected' | 'reconnecting'>('connected');
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [showRefreshSuggestion, setShowRefreshSuggestion] = useState(false);

  // Show refresh suggestion after 30 seconds of disconnection (approximately 10-15 reconnection attempts)
  const REFRESH_SUGGESTION_THRESHOLD = 15;

  useEffect(() => {
    const reconnectTimer: NodeJS.Timeout | null = null;

    const handleConnect = () => {
      setConnectionState('connected');
      setReconnectAttempt(0);
      setDismissed(false);
      setShowRefreshSuggestion(false);
    };

    const handleDisconnect = () => {
      setConnectionState('reconnecting');
      setReconnectAttempt(1);
      setShowRefreshSuggestion(false);
    };

    const handleError = () => {
      setReconnectAttempt((prev) => {
        const newAttempt = prev + 1;
        setConnectionState('reconnecting');

        // Show refresh suggestion after threshold
        if (newAttempt >= REFRESH_SUGGESTION_THRESHOLD) {
          setShowRefreshSuggestion(true);
        }

        return newAttempt;
      });
    };

    wsClient.on('connect', handleConnect);
    wsClient.on('disconnect', handleDisconnect);
    wsClient.on('error', handleError);

    // Check initial state
    if (!wsClient.isConnected()) {
      setConnectionState('disconnected');
    }

    return () => {
      wsClient.off('connect', handleConnect);
      wsClient.off('disconnect', handleDisconnect);
      wsClient.off('error', handleError);
      if (reconnectTimer) clearTimeout(reconnectTimer);
    };
  }, [REFRESH_SUGGESTION_THRESHOLD]);

  const handleRefresh = useCallback(() => {
    window.location.reload();
  }, []);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  // Don't show banner when connected or dismissed
  if (connectionState === 'connected' || dismissed) {
    return null;
  }

  const isReconnecting = connectionState === 'reconnecting';

  return (
    <Collapse in={connectionState !== 'connected'}>
      <Alert
        severity={showRefreshSuggestion ? 'error' : 'warning'}
        icon={<WifiOffIcon />}
        sx={{
          borderRadius: 0,
          ...(position === 'top' && { borderBottom: 1, borderColor: 'divider' }),
          ...(position === 'bottom' && { borderTop: 1, borderColor: 'divider' }),
        }}
        action={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {showRefreshSuggestion && (
              <Button
                color="inherit"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
              >
                {translate('resources.chat.connection.refresh')}
              </Button>
            )}
            <Button color="inherit" size="small" onClick={handleDismiss}>
              {translate('resources.chat.connection.dismiss')}
            </Button>
          </Box>
        }
      >
        <AlertTitle>
          {translate('resources.chat.connection.reconnecting')}
        </AlertTitle>
        {showRefreshSuggestion ? (
          <>
            {translate('resources.chat.connection.prolongedDisconnection')}
            <br />
            {translate('resources.chat.connection.checkConnectionSuggestion')}
          </>
        ) : (
          `${translate('resources.chat.connection.reconnecting')} (${translate('resources.chat.connection.attempt')} ${reconnectAttempt})`
        )}
        {isReconnecting && (
          <LinearProgress
            sx={{ mt: 1, borderRadius: 1 }}
            variant="indeterminate"
          />
        )}
      </Alert>
    </Collapse>
  );
}
